/**
 * Récupération de la configuration ICE auprès du serveur.
 *
 * Le serveur est le seul à connaître l'infrastructure de traversée de NAT : les serveurs
 * STUN à annoncer, et — en Phase 4 — les credentials TURN éphémères. Le client ne code
 * en dur **aucune** adresse : il demande.
 *
 * Corollaire de confidentialité : on ne retombe **jamais** en silence sur un STUN public
 * codé en dur. Ce serait contacter un tiers sans que l'administrateur l'ait décidé.
 * Un échec ici est un échec explicite.
 */

import { IceConfigSchema, type IceConfig } from '@attrape/shared';

const ICE_ENDPOINT = '/ice';
const REQUEST_TIMEOUT_MS = 8000;

export class IceConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IceConfigError';
  }
}

async function requestOnce(signal?: AbortSignal): Promise<IceConfig> {
  const timeoutController = new AbortController();
  const timeout = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);

  // Combine l'annulation appelante et le délai interne.
  const onExternalAbort = (): void => timeoutController.abort();
  signal?.addEventListener('abort', onExternalAbort, { once: true });

  try {
    const response = await fetch(ICE_ENDPOINT, {
      signal: timeoutController.signal,
      headers: { accept: 'application/json' },
      // Les credentials TURN expirent : ne jamais servir une réponse en cache.
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new IceConfigError(`Le serveur a répondu ${response.status}.`);
    }

    const parsed = IceConfigSchema.safeParse(await response.json());
    if (!parsed.success) {
      throw new IceConfigError('Configuration ICE illisible renvoyée par le serveur.');
    }
    if (parsed.data.iceServers.length === 0) {
      throw new IceConfigError('Le serveur n’a annoncé aucun serveur ICE.');
    }

    return parsed.data;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Récupère la configuration ICE, avec une nouvelle tentative.
 *
 * Une seule reprise : au-delà, l'échec est presque toujours structurel (serveur absent,
 * reverse proxy mal configuré) et insister ne fait qu'allonger l'attente de l'utilisateur.
 */
export async function fetchIceConfig(signal?: AbortSignal): Promise<IceConfig> {
  try {
    return await requestOnce(signal);
  } catch (firstError) {
    if (signal?.aborted) throw firstError;
    await new Promise((resolve) => setTimeout(resolve, 700));
    if (signal?.aborted) throw firstError;

    try {
      return await requestOnce(signal);
    } catch {
      throw new IceConfigError(
        'Impossible de joindre le serveur pour établir la connexion. ' +
          'Vérifiez votre réseau, puis rechargez la page.',
      );
    }
  }
}
